import React, { useEffect, useState } from "react";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import { saveAs } from "file-saver";
import { global } from "@shared/libs/Global";

/**
 * 말풍선의 첨부 그림 (aibot-v2-attachments B안, 2026-10-27).
 * - 이 세션에서 보낸 그림은 previewUrl(object URL), 다시 연 방의 그림은 fileId 로 서버에서 받아 온다.
 *   서버 API 는 테넌트 헤더가 있어야 해서 <img src> 로 바로 부르지 않고 받아서 object URL 로 띄운다.
 * - 누르면 크게 보기, 우클릭이면 크게 보기 · 그림 복사 · 그림 저장. 말풍선 우클릭 메뉴(말풍선 캡처)로 번지지 않는다.
 */

const blobUrls = new Map<string, Promise<string | null>>();

/** 받은 그림은 한 번만 받는다(같은 방을 다시 열어도) */
function loadFileUrl(fileId: string): Promise<string | null> {
  let p = blobUrls.get(fileId);
  if (!p) {
    p = fetch(`/snotes/api/chat/v2/attachments/${encodeURIComponent(fileId)}/content`, {
      credentials: "include",
      headers: { "X-TENANT-ID": global.getState().userInfo?.tenant_id || "DEFAULT" },
    })
      .then(async (res) => (res.ok ? URL.createObjectURL(await res.blob()) : null))
      .catch(() => null);
    blobUrls.set(fileId, p);
    /* 실패는 기억하지 않는다 — 다음에 다시 받아 본다 */
    void p.then((u) => {
      if (!u) blobUrls.delete(fileId);
    });
  }
  return p;
}

/**
 * 첨부 파일(PDF)을 새 탭으로 연다. 받아 오는 동안 막히지 않게 빈 탭을 먼저 연다(팝업 차단).
 * 이 세션에서 보낸 것은 previewUrl, 다시 연 방은 fileId 로 받아 온다
 */
export async function openAttachmentFile(fileId?: string, previewUrl?: string): Promise<boolean> {
  const tab = window.open("", "_blank");
  const url = previewUrl ?? (fileId ? await loadFileUrl(fileId) : null);
  if (!url) {
    tab?.close();
    return false;
  }
  if (tab) tab.location.href = url;
  else window.open(url, "_blank");
  return true;
}

/** 클립보드는 png 만 받는다 — jpeg 면 그려서 png 로 바꾼다 */
async function toPngBlob(src: string): Promise<Blob> {
  const blob = await (await fetch(src)).blob();
  if (blob.type === "image/png") return blob;
  const img = new Image();
  img.src = src;
  await img.decode();
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  canvas.getContext("2d")?.drawImage(img, 0, 0);
  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("png"))), "image/png")
  );
}

type Props = {
  name: string;
  previewUrl?: string;
  fileId?: string;
  /** 복사 · 저장 결과를 화면 안내로 */
  onNotice?: (message: string) => void;
};

const ChatAttachmentImage: React.FC<Props> = ({ name, previewUrl, fileId, onNotice }) => {
  const [src, setSrc] = useState<string | null>(previewUrl ?? null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (previewUrl) {
      setSrc(previewUrl);
      return;
    }
    if (!fileId) return;
    let alive = true;
    void loadFileUrl(fileId).then((u) => {
      if (!alive) return;
      if (u) setSrc(u);
      else setFailed(true);
    });
    return () => {
      alive = false;
    };
  }, [previewUrl, fileId]);

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setZoom(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  if (!src) {
    return (
      <span className="chatbot-attachment-thumb chatbot-attachment-thumb-empty" title={failed ? "그림을 찾을 수 없습니다" : name}>
        {failed ? "없음" : "…"}
      </span>
    );
  }

  const copy = async () => {
    setMenu(null);
    try {
      const png = await toPngBlob(src);
      await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
      onNotice?.("그림을 복사했습니다.");
    } catch {
      onNotice?.("이 주소에서는 그림을 복사할 수 없습니다. 저장해서 쓰세요.");
    }
  };

  const save = async () => {
    setMenu(null);
    try {
      saveAs(await (await fetch(src)).blob(), name || "그림.png");
    } catch {
      onNotice?.("그림을 저장하지 못했습니다.");
    }
  };

  return (
    <>
      <img
        alt={name}
        className="chatbot-attachment-thumb chatbot-attachment-thumb-lg"
        onClick={(e) => {
          e.stopPropagation();
          setZoom(true);
        }}
        onContextMenu={(e) => {
          /* 말풍선 메뉴(말풍선 캡처)가 아니라 그림 메뉴 */
          e.preventDefault();
          e.stopPropagation();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        src={src}
        title="누르면 크게 봅니다 · 우클릭하면 복사 · 저장"
      />
      <Menu
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
        anchorReference="anchorPosition"
        onClose={() => setMenu(null)}
        open={menu !== null}
      >
        <MenuItem dense onClick={() => { setMenu(null); setZoom(true); }}>
          <ListItemIcon><ZoomInIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>크게 보기</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => void copy()}>
          <ListItemIcon><ContentCopyIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>그림 복사</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => void save()}>
          <ListItemIcon><DownloadIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>그림 저장</ListItemText>
        </MenuItem>
      </Menu>
      {zoom && (
        <div className="chatbot-image-lightbox" onClick={() => setZoom(false)} role="dialog" aria-label={name}>
          <img
            alt={name}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenu({ x: e.clientX, y: e.clientY });
            }}
            src={src}
          />
          <div className="chatbot-image-lightbox-bar" onClick={(e) => e.stopPropagation()}>
            <span className="chatbot-image-lightbox-name">{name}</span>
            <button onClick={() => void copy()} type="button"><ContentCopyIcon fontSize="inherit" /> 복사</button>
            <button onClick={() => void save()} type="button"><DownloadIcon fontSize="inherit" /> 저장</button>
            <button aria-label="닫기" onClick={() => setZoom(false)} type="button"><CloseIcon fontSize="inherit" /></button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatAttachmentImage;
